"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Award, Download } from "lucide-react";

import { Button } from "@/components/ui/button";
import { downloadContestCertificate } from "../api";
import { useContestCertificates } from "../hooks";
import type { ContestCertificateRecipient, PublicContestDetail } from "../types";
import { certificateErrorKey } from "./organizer/CertificatesCard";

/**
 * "Your certificate" (`EX-DAY06`, `docs/29` §6.12), on a finished contest's
 * door, for somebody who competed on a contest that issues certificates. It
 * waits for the final results, then lists what this account may download:
 * one certificate for a contestant, one per person on the roster for an
 * issued login (the only account those people have). The download carries
 * the bearer, so an issued login, which can open only this page, gets them
 * here. The post-contest email links `#certificate`.
 */
export function ContestCertificateDoor({ contest }: { contest: PublicContestDetail }) {
  const t = useTranslations("contest.page.certificate");
  const viewer = contest.viewer?.certificate ?? null;
  const ready = viewer?.state === "ready";
  const list = useContestCertificates(contest.slug, ready);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const anchor = useRef<HTMLElement>(null);

  useEffect(() => {
    if (viewer && typeof window !== "undefined" && window.location.hash === "#certificate") {
      anchor.current?.scrollIntoView({ block: "center" });
    }
  }, [viewer]);

  if (!viewer) return null;

  const recipients = list.data?.recipients ?? [];
  const state = ready ? list.data?.state : "board_not_final";

  const download = async (recipient: ContestCertificateRecipient) => {
    setError(null);
    setDownloading(recipient.recipientId);
    try {
      await downloadContestCertificate(contest.slug, recipient.recipientId, `${recipient.name}.pdf`);
      void list.refetch();
    } catch (err) {
      const key = certificateErrorKey(err);
      setError(key === "errors.still_judging" ? t("judging") : t("failed"));
    } finally {
      setDownloading(null);
    }
  };

  let body: React.ReactNode;
  if (state === "board_not_final" || state === undefined) {
    body = ready && list.isLoading ? (
      <div className="mt-3 h-10 animate-pulse rounded-[10px] bg-white/10" aria-hidden />
    ) : (
      <p className="mt-2 text-sm leading-relaxed text-neutral-300">{t("waiting")}</p>
    );
  } else if (state === "still_judging") {
    body = <p className="mt-2 text-sm leading-relaxed text-neutral-300">{t("judging")}</p>;
  } else if (recipients.length === 0) {
    body = <p className="mt-2 text-sm leading-relaxed text-neutral-300">{t("none")}</p>;
  } else {
    body = (
      <ul className="mt-3 grid gap-2.5" data-testid="contest-certificate-list">
        {recipients.map((recipient) => (
          <li
            key={recipient.recipientId}
            className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-white/10 px-3 py-2.5"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">{recipient.name}</p>
              <p className="truncate text-xs text-neutral-400">{recipient.placement ?? t("participation")}</p>
            </div>
            <div className="flex items-center gap-3">
              {recipient.certificate ? (
                <a
                  href={recipient.certificate.verificationUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-neutral-300 underline-offset-2 hover:text-white hover:underline"
                >
                  {t("verify")}
                </a>
              ) : null}
              <Button
                type="button"
                variant="inverse"
                onClick={() => void download(recipient)}
                disabled={downloading !== null}
              >
                <Download className="h-4 w-4" aria-hidden />
                {t("download")}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section
      id="certificate"
      ref={anchor}
      aria-labelledby="contest-certificate-title"
      className="mt-5 scroll-mt-24 border-t border-white/10 pt-5"
    >
      <p id="contest-certificate-title" className="flex items-center gap-2 text-sm font-bold text-white">
        <Award className="h-4 w-4 text-accent-peach" aria-hidden />
        {recipients.length > 1 ? t("titleMany") : t("title")}
      </p>
      {body}
      {error ? (
        <p role="alert" className="mt-3 rounded-lg border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs text-red-200">
          {error}
        </p>
      ) : null}
    </section>
  );
}
